import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Row, Col } from 'reactstrap';
import ModalPopup from 'components/organisms/modal';
import Button from 'components/atoms/button';
import Input from 'components/atoms/input';
import forms from 'constants/forms';
import { format } from 'date-fns';
import { showResult, getInitialValues } from 'utils';
import * as Yup from 'yup';
import common from 'constants/common';
import { useDispatch } from 'react-redux';

const PROFILE_FIELDS = [
  forms.profileFields[0], // first name
  forms.profileFields[1], // last name
  forms.profileFields[2], // middle name
  forms.profileFields[3], // email
  forms.profileFields[4], // primary number
  forms.profileFields[16], // secondary number
  forms.profileFields[5], // address
  forms.profileFields[13], // city
  forms.profileFields[14], // state
  forms.profileFields[15], // zip
  forms.profileFields[6], // dob
  forms.profileFields[7], // marital status
  forms.profileFields[8], // working status
  forms.profileFields[9], // race
  forms.profileFields[10], // gender
  forms.profileFields[11], // timezone
  forms.profileFields[12], // emergency contact number
];
const PatientInfoSchema = Yup.object().shape(
  {
    firstName: Yup.string()
      .required('required')
      .test(
        'is-not-only-spaces',
        'First Name is not valid',
        (value) => !!value && value.trim().length > 0,
      ),
    lastName: Yup.string()
      .required('required')
      .test(
        'is-not-only-spaces',
        'Last Name is not valid',
        (value) => !!value && value.trim().length > 0,
      ),
    phone: Yup.string()
      .matches(/^[+][0-9]*$/, 'Phone number is not correct')
      .required('required'),
    homeNumber: Yup.string()
      .matches(/^[+][0-9]*$/, 'Home number is not correct')
      .nullable(),
    birthDate: Yup.string()
      .matches(
        /^(0[1-9]|1[012])[-]([012][0-9]|3[01])[-]([0-9]{4})$/,
        'Birth Date must be in the format MM-DD-YYYY.',
      )
      .required('required'),
    emergencyContactNumber: Yup.string()
      .matches(/^[+][0-9]*$/, 'Phone number is not correct')
      .nullable(),
    email: Yup.string().email('Email is not correct').required('required'),
    address: Yup.string().when('zipPostalCode', {
      is: (zipPostalCode) => !!zipPostalCode,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    city: Yup.string().when('zipPostalCode', {
      is: (zipPostalCode) => !!zipPostalCode,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    state: Yup.string().when(['zipPostalCode', 'city'], {
      is: (zipPostalCode, city) => !!zipPostalCode || !!city,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
    zipPostalCode: Yup.string().when(['address', 'state'], {
      is: (address, state) => !!address || !!state,
      then: (schema) => schema.required('required').nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  },
  [
    ['address', 'zipPostalCode'],
    ['zipPostalCode', 'state'],
  ],
);
const EditProfileModal = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  submit,
  profile,
  isRpmPanel,
}) => {
  const {
    ccmPatientDetails: { getPatientDetails },
  } = useDispatch();

  const handleSubmit = async (values) => {
    const postData = {
      guid: patientGuid,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      middleName: values.middleName?.trim(),
      email: values.email,
      phone: values.phone,
      homeNumber: values.homeNumber,
      address: values.address?.trim(),
      city: values.city?.trim(),
      state: values.state?.trim(),
      zipPostalCode: values.zipPostalCode?.trim(),
      birthDate: values.birthDate,
      martialStatus: values.martialStatus,
      workingStatus: values.workingStatus,
      race: values.race?.trim(),
      gender: values.gender,
      timezone: values.timezone,
      emergencyContactNumber: values.emergencyContactNumber,
    };
    const { error } = await submit(postData);
    if (!isRpmPanel) await getPatientDetails(patientGuid);

    if (error) showResult(error);
    else showResult(null, common.submitSuccessTypes.changed, toggle);
  };
  const normalizedProfile = { ...profile };
  const birthDay = new Date(profile.birthDate);
  if (birthDay && !Number.isNaN(birthDay.getTime())) {
    normalizedProfile.birthDate = format(
      birthDay,
      common.dateFormats.birthDate,
    );
  }

  return (
    <ModalPopup
      isOpen={isOpen}
      toggle={toggle}
      title="Edit Profile"
      className="popup edit-profile-popup"
    >
      <Formik
        initialValues={getInitialValues(PROFILE_FIELDS, normalizedProfile)}
        onSubmit={handleSubmit}
        validationSchema={PatientInfoSchema}
      >
        {({ errors }) => (
          <Form className="user--form">
            <Row>
              {PROFILE_FIELDS.map((field, index) => (
                <Col key={index} lg="3" md="4" sm="6" xs="12">
                  <Input {...field} />
                  {errors[field.id] ? (
                    <div className="input-error">{errors[field.id]}</div>
                  ) : null}
                </Col>
              ))}
            </Row>
            <div className="text-center">
              <Button type="submit" text="Save" disabled={loading} />
            </div>
          </Form>
        )}
      </Formik>
    </ModalPopup>
  );
};
EditProfileModal.defaultProps = {
  isOpen: false,
  profile: {},
};

EditProfileModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  patientGuid: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  profile: PropTypes.shape(),
  patient: PropTypes.shape(),
  isRpmPanel: PropTypes.bool,
};

export default EditProfileModal;
